import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { audit, sync } from "@/api/endpoints";
import { Spinner } from "@/components/shared/Spinner";
import { useToast } from "@/components/shared/Toast";
import { UserManagement } from "./UserManagement";

export function AdminPage() {
  const showToast = useToast((s) => s.show);
  const qc = useQueryClient();

  const runs = useQuery({ queryKey: ["sync", "runs"], queryFn: () => sync.runs() });
  const auditLog = useQuery({ queryKey: ["audit", "recent"], queryFn: () => audit.list({ limit: 50 }) });

  const triggerVast = useMutation({
    mutationFn: () => sync.triggerVastMachineId(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sync"] });
      showToast("Reconciliation run completed.", "success");
    },
    onError: (e) => showToast(e instanceof Error ? e.message : "failed", "error"),
  });
  const triggerRollup = useMutation({
    mutationFn: () => sync.triggerEarningsRollup(false, 7),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sync"] });
      showToast("Earnings rollup completed.", "success");
    },
    onError: (e) => showToast(e instanceof Error ? e.message : "failed", "error"),
  });

  const [conflictRunId, setConflictRunId] = useState<string | null>(null);
  const conflicts = useQuery({
    queryKey: ["sync", "conflicts", conflictRunId],
    queryFn: () => sync.conflicts(conflictRunId!),
    enabled: !!conflictRunId,
  });

  return (
    <div className="space-y-8">
      <UserManagement />

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold">Sync</h1>
          <div className="flex gap-2">
            <button
              onClick={() => triggerVast.mutate()}
              disabled={triggerVast.isPending}
              className="rounded-md bg-brand-500 px-3 py-1.5 text-sm text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {triggerVast.isPending ? "Running…" : "Sync Vast machine IDs"}
            </button>
            <button
              onClick={() => triggerRollup.mutate()}
              disabled={triggerRollup.isPending}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800"
            >
              {triggerRollup.isPending ? "Running…" : "Rebuild earnings rollup"}
            </button>
          </div>
        </div>

        {runs.isLoading ? (
          <Spinner />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-800">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">
                <tr>
                  <th className="px-3 py-2 text-left">Job</th>
                  <th className="px-3 py-2 text-left">Started</th>
                  <th className="px-3 py-2 text-left">Trigger</th>
                  <th className="px-3 py-2 text-right">Seen</th>
                  <th className="px-3 py-2 text-right">Updated</th>
                  <th className="px-3 py-2 text-right">Conflicts</th>
                  <th className="px-3 py-2 text-left">Error</th>
                </tr>
              </thead>
              <tbody>
                {(runs.data ?? []).map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="px-3 py-2">{r.job_name}</td>
                    <td className="px-3 py-2 text-slate-500">{new Date(r.started_at).toLocaleString()}</td>
                    <td className="px-3 py-2">{r.triggered_by}</td>
                    <td className="px-3 py-2 text-right">{r.devices_seen}</td>
                    <td className="px-3 py-2 text-right">{r.devices_updated}</td>
                    <td className="px-3 py-2 text-right">
                      {r.conflicts > 0 ? (
                        <button
                          onClick={() => setConflictRunId(r.id)}
                          className="text-amber-700 hover:underline"
                        >
                          {r.conflicts}
                        </button>
                      ) : (
                        0
                      )}
                    </td>
                    <td className="px-3 py-2 text-red-600">{r.error_message || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {conflictRunId && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm dark:bg-amber-950/30 dark:border-amber-900">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium">Conflicts for run {conflictRunId.slice(0, 8)}</h3>
              <button
                onClick={() => setConflictRunId(null)}
                className="text-xs text-slate-500 hover:text-slate-900"
              >
                close
              </button>
            </div>
            {conflicts.isLoading ? (
              <Spinner />
            ) : (
              <table className="min-w-full text-xs">
                <thead className="text-slate-600">
                  <tr>
                    <th className="text-left">Type</th>
                    <th className="text-left">Host</th>
                    <th className="text-right">Vast machine_id</th>
                    <th className="text-right">Netbox device</th>
                    <th className="text-right">Existing value</th>
                  </tr>
                </thead>
                <tbody>
                  {(conflicts.data ?? []).map((c) => (
                    <tr key={c.id} className="border-t border-amber-200/50">
                      <td>{c.conflict_type}</td>
                      <td>{c.host}</td>
                      <td className="text-right">{c.vast_machine_id ?? "—"}</td>
                      <td className="text-right">{c.netbox_device_id ?? "—"}</td>
                      <td className="text-right">{c.netbox_existing_value ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h1 className="text-lg font-semibold">Audit log (recent 50)</h1>
        {auditLog.isLoading ? (
          <Spinner />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-800">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Actor</th>
                  <th className="px-3 py-2 text-left">Action</th>
                  <th className="px-3 py-2 text-left">Target</th>
                  <th className="px-3 py-2 text-left">Reason</th>
                </tr>
              </thead>
              <tbody>
                {(auditLog.data ?? []).map((e) => (
                  <tr key={e.id} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                      {new Date(e.occurred_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">{e.actor_kind === "system" ? "system" : (e.actor_user_id ?? "?").slice(0, 8)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{e.action}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {e.target_kind}/{e.target_id}
                    </td>
                    <td className="px-3 py-2 text-slate-500">{e.reason ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
